import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom'; // Import useNavigate and Link
import CopyableId from '../common/CopyableId'; // Import CopyableId component
import SaveButton from '../common/SaveButton'; // Import SaveButton component
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const SeasonsEpisodes = ({ programId, selectedSeason, onSeasonChange, highlightedEpisode }) => { // Accept programId, selectedSeason, onSeasonChange, and highlightedEpisode as props
  const { t } = useAppContext();
  const navigate = useNavigate(); // Initialize navigate hook
  const [activeSeason, setActiveSeason] = useState(selectedSeason);
  const [seasons, setSeasons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [episodesData, setEpisodesData] = useState([]);
  const [allEpisodes, setAllEpisodes] = useState([]); // New state for all episodes

  const getSeasonIdentifier = (season) => {
    if (!season) return null;
    return season.seasonNumber != null ? season.seasonNumber : season.seasonYear;
  };

  const getSeasonOrderValue = (season) => {
    const identifier = getSeasonIdentifier(season);
    return identifier != null ? identifier : Number.MAX_SAFE_INTEGER;
  };

  // Format date to be more readable
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return t('programSummary.unknown');
    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Handle clicking on an episode title - update parent URL before navigating
  const handleEpisodeClick = (episodeId, e) => {
    if (episodeId && e) {
      e.preventDefault(); // Prevent default link behavior
      
      // First, update the current URL to include the episode parameter
      // This ensures when we navigate back, we return to this exact state
      const params = { tab: 'seasonsEpisodes', season: String(activeSeason), episode: episodeId };
      window.history.replaceState(null, '', `?${new URLSearchParams(params).toString()}`);
      
      // Now navigate to the episode page
      navigate(`/program/${episodeId}`);
    }
  };

  // Handle season change
  const handleSeasonChange = (season) => {
    setActiveSeason(season);
    if (onSeasonChange) {
      onSeasonChange(season);
    }
  };

  // Update activeSeason when selectedSeason prop changes (from URL)
  useEffect(() => {
    if (selectedSeason !== null && selectedSeason !== undefined) {
      // Normalize both values for comparison (convert to same type)
      const normalizedSelected = typeof selectedSeason === 'string' ? (isNaN(selectedSeason) ? selectedSeason : parseInt(selectedSeason, 10)) : selectedSeason;
      const normalizedActive = typeof activeSeason === 'string' ? (isNaN(activeSeason) ? activeSeason : parseInt(activeSeason, 10)) : activeSeason;
      
      if (normalizedSelected !== normalizedActive) {
        setActiveSeason(normalizedSelected);
      }
    }
  }, [selectedSeason]);

  // Fetch seasons data when component mounts or when programId changes
  const fetchSeasons = useCallback(async () => {
    if (!programId) return;

    setIsLoading(true);
    setError(null);
    setSeasons([]); // Clear previous seasons
    setActiveSeason(null); // Reset active season
    setAllEpisodes([]); // Clear previous all episodes

    try {
      const seasonsData = await apiService.getSeasons(programId); // Use programId prop

      if (seasonsData && seasonsData.length > 0) {
        // Sort seasons by seasonNumber, falling back to seasonYear when seasonNumber is null
        const sortedSeasons = [...seasonsData].sort((a, b) => getSeasonOrderValue(a) - getSeasonOrderValue(b));
        setSeasons(sortedSeasons);

        // Fetch all episodes for all seasons
        const allEpisodesPromises = sortedSeasons.map(async (season) => {
          const episodes = await apiService.getSeasonEpisodes(season.seasonId, programId);
          return episodes.map(episode => ({
            id: episode.TMSId,
            seasonNumber: getSeasonIdentifier(season), // Use seasonNumber or fall back to seasonYear
            number: episode.epinfo?.number || 'N/A',
            title: episode.epinfo?.title || t('savedItems.unknownTitle'),
            lang: episode.epinfo?.lang || '',
            description: episode.description || '',
            airDate: formatDate(episode.origAirDate),
            duration: episode.duration ? t('lineups.duration.min', { minutes: episode.duration }) : t('programSummary.unknown')
          }));
        });
        const allEpisodesArrays = await Promise.all(allEpisodesPromises);
        const combinedEpisodes = allEpisodesArrays.flat();
        setAllEpisodes(combinedEpisodes);

        // Set the first season as active by default, or use selectedSeason from URL if available
        const firstSeasonIdentifier = getSeasonIdentifier(sortedSeasons[0]);
        if (selectedSeason !== null && selectedSeason !== undefined) {
          // Normalize selected season for comparison
          const normalizedSelected = typeof selectedSeason === 'string' ? (isNaN(selectedSeason) ? selectedSeason : parseInt(selectedSeason, 10)) : selectedSeason;
          
          // Validate that the selectedSeason exists in the list
          const seasonExists = sortedSeasons.some(s => getSeasonIdentifier(s) === normalizedSelected);
          if (seasonExists) {
            setActiveSeason(normalizedSelected);
          } else {
            setActiveSeason(firstSeasonIdentifier);
            if (onSeasonChange) {
              onSeasonChange(firstSeasonIdentifier);
            }
          }
        } else {
          setActiveSeason(firstSeasonIdentifier);
          if (onSeasonChange) {
            onSeasonChange(firstSeasonIdentifier);
          }
        }
      } else {
        // No seasons were returned; attempt to fetch episodes not tied to a specific season
        setSeasons([]);

        const episodesNoSeries = await apiService.getSeasonEpisodesNoSeries(null, programId);

        if (episodesNoSeries && episodesNoSeries.length > 0) {
          const mappedEpisodes = episodesNoSeries.map(episode => ({
            id: episode.TMSId,
            // Use season/seasonYear from epinfo if present, otherwise mark as N/A
            seasonNumber: episode.epinfo?.season ?? episode.epinfo?.seasonYear ?? 'N/A',
            number: episode.epinfo?.number || 'N/A',
            title: episode.epinfo?.title || t('savedItems.unknownTitle'),
            lang: episode.epinfo?.lang || '',
            description: episode.description || '',
            airDate: formatDate(episode.origAirDate),
            duration: episode.duration ? t('lineups.duration.min', { minutes: episode.duration }) : t('programSummary.unknown')
          }));
          setAllEpisodes(mappedEpisodes);
        }
      }
    } catch (err) {
      console.error('Failed to fetch seasons data:', err);
      setError(t('seasonsEpisodes.error'));
    } finally {
      setIsLoading(false);
    }
  }, [programId, t]); // Removed formatDate from dependency array

  // Fetch seasons on initial load or when programId changes
  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  // Update episodesData when activeSeason changes
  useEffect(() => {
    // If we have defined seasons, keep the existing season-based filtering behaviour
    if (seasons.length > 0) {
      if (activeSeason !== null && allEpisodes.length > 0) {
        const filtered = allEpisodes.filter(episode => episode.seasonNumber === activeSeason);
        // Sort by episode number
        const sorted = filtered.sort((a, b) => {
          const numA = a.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(a.number, 10);
          const numB = b.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(b.number, 10);
          return numA - numB;
        });
        setEpisodesData(sorted);
      } else if (allEpisodes.length > 0) {
        // If activeSeason is null (initial load), set episodes for the first season
        const firstSeasonIdentifier = getSeasonIdentifier(seasons[0]);
        const firstSeasonEpisodes = allEpisodes.filter(episode => episode.seasonNumber === firstSeasonIdentifier);
        const sorted = firstSeasonEpisodes.sort((a, b) => {
          const numA = a.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(a.number, 10);
          const numB = b.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(b.number, 10);
          return numA - numB;
        });
        setEpisodesData(sorted);
      } else {
        setEpisodesData([]);
      }
    } else {
      // Fallback: no seasons but we may have episodes returned by getSeasonEpisodesNoSeries
      if (allEpisodes.length > 0) {
        const sorted = [...allEpisodes].sort((a, b) => {
          const numA = a.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(a.number, 10);
          const numB = b.number === 'N/A' ? Number.MAX_SAFE_INTEGER : parseInt(b.number, 10);
          return numA - numB;
        });
        setEpisodesData(sorted);
      } else {
        setEpisodesData([]);
      }
    }
  }, [activeSeason, allEpisodes, seasons]); // Added seasons to dependency array

  // Get active season data
  const getActiveSeasonData = () => {
    if (!activeSeason || !seasons.length) return null;
    return seasons.find(season => getSeasonIdentifier(season) === activeSeason);
  };

  const activeSeasonData = getActiveSeasonData();

  // Initial Loading - only show full loading screen for initial load
  if (isLoading && !seasons.length) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('seasonsEpisodes.loadingSeasons')}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchSeasons} // Retry fetching seasons
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Render empty state (only when we have neither seasons nor any episodes)
  if ((!seasons || seasons.length === 0) && allEpisodes.length === 0) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('seasonsEpisodes.noData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 pb-16 overflow-auto">
      <div className="flex mb-6">
        <h3 className="text-gray-500 text-sm mr-4">{t('seasonsEpisodes.title')}</h3>
        <div className="flex flex-wrap gap-2">
          {seasons.map((season) => (
            <button
              key={season.seasonId}
              className={`w-10 h-10 text-sm font-medium rounded-full flex items-center justify-center ${
                activeSeason === getSeasonIdentifier(season)
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => handleSeasonChange(getSeasonIdentifier(season))}
            >
              {season.seasonNumber != null ? season.seasonNumber : season.seasonYear}
            </button>
          ))}
        </div>
      </div>

      {seasons.length === 0 && allEpisodes.length > 0 && (
        <div className="mb-4 text-sm text-gray-600">
          {t('seasonsEpisodes.episodesNoSeries')}
        </div>
      )}

      {activeSeasonData && (
        <div className="mb-4">
          <div className="mb-3">
            <h2 className="text-lg font-semibold inline-block">
              {t('seasonsEpisodes.seasonHeader', { number: activeSeasonData.seasonNumber != null ? activeSeasonData.seasonNumber : activeSeasonData.seasonYear })}
            </h2>
            <span className="ml-2 text-sm text-gray-500">
              {t('seasonsEpisodes.seasonInfo', {
                count: activeSeasonData.totalSeasonEpisodes,
                year: activeSeasonData.seasonPremiere?.substring(0, 4) || t('programSummary.unknown'),
                network: activeSeasonData.origNetwork || t('programSummary.unknown'),
                id: activeSeasonData.seasonId,
                premiere: formatDate(activeSeasonData.seasonPremiere),
                finale: formatDate(activeSeasonData.seasonFinale)
              })}
            </span>
          </div>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="bg-gray-100 text-left">
          <tr>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.number')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.title')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.description')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.airDate')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.duration')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.lang')}</th>
            <th className="px-4 py-2 font-medium text-gray-600">{t('seasonsEpisodes.columns.tmsId')}</th>
          </tr>
        </thead>
      <tbody>
          {episodesData.length > 0 ? (
            episodesData.map((episode) => (
              <tr 
                key={episode.id} 
                className={`border-t border-gray-200 hover:bg-blue-50 ${
                  highlightedEpisode === episode.id ? 'bg-yellow-100 hover:bg-yellow-200' : ''
                }`}
              >
                <td className="px-4 py-3 text-left">{episode.number}</td>
                <td className="px-4 py-3 text-left">
                  <Link
                    to={`/program/${episode.id}`}
                    onClick={(e) => handleEpisodeClick(episode.id, e)}
                    className="text-blue-600 hover:underline text-left cursor-pointer"
                  >
                    {episode.title}
                  </Link>
                </td>
                <td className="px-4 py-3 text-left max-w-md">
                  <span className="text-gray-700">{episode.description}</span>
                </td>
                <td className="px-4 py-3 text-left">{episode.airDate}</td>
                <td className="px-4 py-3 text-left">{episode.duration}</td>
                <td className="px-4 py-3 text-left">{episode.lang}</td>
                <td className="px-4 py-3 text-left">
                  {episode.id ? (
                    <div className="flex items-center">
                      <CopyableId 
                        value={episode.id} 
                        onClickOverride={(e) => { 
                          e.stopPropagation(); 
                          handleEpisodeClick(episode.id); 
                        }} 
                      />
                      <div className="ml-2">
                        <SaveButton tmsId={episode.id} />
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr className="border-t border-gray-200">
              <td colSpan="6" className="px-4 py-8 text-left text-gray-500">
                {isLoading ? t('seasonsEpisodes.loadingEpisodes') : t('seasonsEpisodes.noSeasonEpisodes')}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-6 flex justify-between text-xs text-gray-500">
        <div>
          {seasons.length > 0
            ? t('seasonsEpisodes.showingFull', { count: episodesData.length, total: activeSeasonData ? activeSeasonData.totalSeasonEpisodes : '?' })
            : t('seasonsEpisodes.showing', { count: episodesData.length })}
        </div>
        <div>
          <button
            className={`px-3 py-1 border border-gray-300 rounded-sm ${allEpisodes.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            onClick={() => exportToCsv(allEpisodes, `${programId}_all_episodes.csv`)}
            disabled={allEpisodes.length === 0}
          >
            {t('seasonsEpisodes.export')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeasonsEpisodes;
